"use client";

import React, { useState, useEffect } from "react";
import { AnimatedSection } from "@hooks/useSmoothScrollAnimations";

interface FadeInUpProps {
  children: React.ReactNode;
  delay?: number;
  duration?: "fast" | "normal" | "slow";
  threshold?: number;
  rootMargin?: string;
  className?: string;
  style?: React.CSSProperties;
  id?: string;
}

// Global counter for hydration-safe IDs
let idCounter = 0;

const FadeInUp: React.FC<FadeInUpProps> = ({
  children,
  delay = 0,
  duration = "normal",
  threshold = 0.1,
  rootMargin = "50px 0px 50px 0px",
  className = "",
  style = {},
  id,
}) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [animationId] = useState(() => id || `fade-in-up-${++idCounter}`);

  // Hydration detection
  useEffect(() => {
    setIsHydrated(true);
  }, []);

  // Map duration to actual delay values
  const durationDelay = {
    fast: 0,
    normal: delay,
    slow: delay + 0.2,
  }[duration];

  // Server-side: Render without animation
  if (!isHydrated) {
    return (
      <div className={className} style={style}>
        {children}
      </div>
    );
  }

  // Client-side: GPU-accelerated animation
  return (
    <AnimatedSection
      id={animationId}
      animation="fadeInUp"
      className={`${className} will-change-transform`}
      config={{
        threshold,
        rootMargin,
        delay: durationDelay,
        duration: duration === "fast" ? 0.4 : duration === "slow" ? 0.8 : 0.6,
      }}
    >
      <div
        style={{
          ...style,
          transform: "translateZ(0)", // Force GPU compositing
          backfaceVisibility: "hidden",
        }}
      >
        {children}
      </div>
    </AnimatedSection>
  );
};

export default FadeInUp;
